/**
 * @author Madhuri <sai.madhuri@314ecorp.com>
 * @description Batch Eligibility Requests Resubmission
 */
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, Divider, Modal, Row, Col, Alert, Spin, Space, Tooltip } from 'antd';

import { Redo } from '@icon-park/react';

import { RangePicker } from 'lib/antd';
import dateParser from 'lib/dateParser';
import { headerTextStyle, ServiceDateOptions, renderFilterTags, getAllFilterKeysFromQuery } from './ExportEligibilityData';
import { InProgressBatchAlertBar } from 'components/eligibility/batchUpload/dataProcessing/CustomComponents';
import { IN_PROGRESS_QUERY_PARAMS } from 'components/eligibility/requests/EligibilityFilter';
import { getCoverageRule } from 'hooks/eligibility/useAllEligibilityRequests';
import useValidateBatchEligibilityResubmission from 'hooks/eligibility/mutate/useValidateBatchEligibilityResubmission';
import usePostBatchEligibilityResubmission from 'hooks/eligibility/mutate/usePostBatchEligibilityResubmission';
import useBatchEligibilityRequestHistory from 'hooks/eligibility/useBatchEligibilityRequestHistory';
import { customDatePresets } from 'utils/dateParser';
import { searchParamsKeys } from 'utils/router';
import { prepareDateRange, dataKeys } from 'utils/eligibility/eligibilityRequests';
import { getStatus } from 'utils/eligibility/eligibilityResponse';
import { parseQueryFilterParams } from 'components/forms/schema/claimsAdvancedFilterSchema';
import { hasIncompleteBatch } from 'utils/eligibility/eligibilityBatchUpload';
import { YEAR_MONTH_DATE_FORMAT } from '@veritable/common/constants';
import { ELIGIBILITY_RESUBMIT_INVALID_STATUS, ELIGIBILITY_RESPONSE_TAG_STATUS, RED_HEX_CODE } from 'app-constants/eligibility';
import { insertRuleIntoGroup, prepareQueryGroup, prepareQueryRule, removeRuleFromGroup } from 'utils/queryBuilder';
import { OPERATORS } from 'lib/SearchQueryBuilder/config/queryConfig';
import { JsonRule, JsonTree } from '@react-awesome-query-builder/antd';

const getCountMessage = (count: number): string => {
	if (count) {
		return `Number of valid resubmissions: ${count}`;
	}
	return 'No valid resubmissions available based on your applied filters.';
};

const getBatchIdRule = (batchID: string): JsonRule => {
	return prepareQueryRule({
		field: dataKeys.batchId,
		operator: OPERATORS.equal,
		value: [batchID],
	});
};

const getDateRangeRule = (dateRange: [string, string]): JsonRule => {
	return prepareQueryRule({
		field: dataKeys.dateOfService,
		operator: OPERATORS.between,
		value: dateRange,
		valueSrc: ['value', 'value'],
		valueType: ['text', 'text'],
	});
};

const getInquiryDateRule = (): JsonRule => {
	return prepareQueryRule({
		field: dataKeys.inquiryDate,
		operator: OPERATORS.between,
		value: [
			dateParser().subtract(2, 'months').add(1, 'day').format(YEAR_MONTH_DATE_FORMAT),
			dateParser().format(YEAR_MONTH_DATE_FORMAT),
		],
		valueSrc: ['value', 'value'],
		valueType: ['text', 'text'],
	});
};

export interface IProps {
	batchID?: string;
	storeValues: EligibilityRequests.IValues;
	storeActions: EligibilityRequests.IActions;
	'data-testid'?: string;
}

/**
 * BatchResubmitEligibilityRequests
 */
const BatchResubmitEligibilityRequests: React.FC<IProps> = ({ batchID, storeValues, storeActions, 'data-testid': dataTestId }) => {
	const { apiParams, batchResubmitMetaData } = storeValues;
	const { setBatchResubmitMetaData, closeBatchResubmissionModal } = storeActions;
	const { mutate, isPending } = usePostBatchEligibilityResubmission();
	const { search } = useLocation();
	const coverageId = new URLSearchParams(search)?.get(searchParamsKeys.coverageId) || '';

	const previousBatches = useBatchEligibilityRequestHistory(
		IN_PROGRESS_QUERY_PARAMS.filterParams,
		IN_PROGRESS_QUERY_PARAMS.pageNumber,
		IN_PROGRESS_QUERY_PARAMS.pageSize,
		{ enabled: batchResubmitMetaData.visible },
	);
	const { hasInprogressBatch, hasScheduledBatch } = hasIncompleteBatch(previousBatches.data);

	const blockResubmit = hasInprogressBatch || hasScheduledBatch;

	// function to prepare initial meta based on api params
	const { filterParams, hasFilterParams, fieldValuesSet } = useMemo(() => {
		let baseGroup: JsonTree =
			apiParams.criteria && apiParams.criteria.type === 'group'
				? _.cloneDeep(apiParams.criteria) // safe clone to avoid mutating props
				: prepareQueryGroup({ children: apiParams.criteria ? [apiParams.criteria] : [] });

		const allFieldValues = _.uniq(getAllFilterKeysFromQuery({ criteria: baseGroup }));
		const hasParams = !_.isEmpty(allFieldValues);

		// add dateOfService rule only when no extra filters are applied
		if (!hasParams) {
			baseGroup = insertRuleIntoGroup(baseGroup, getDateRangeRule(batchResubmitMetaData.dateRange.range));
		}

		// append batchId rule
		if (batchID) {
			baseGroup = insertRuleIntoGroup(baseGroup, getBatchIdRule(batchID));
		}

		// scope resubmit to the coverage when viewing a coverage-specific list (mirrors useAllEligibilityRequests)
		if (coverageId) {
			baseGroup = insertRuleIntoGroup(baseGroup, getCoverageRule(coverageId));
		}

		// remove inquiryDate rule if it exists
		// TODO - conflict behavior between advanced filters & resubmit only last 60 days. Discuss with product team
		if (_.includes(allFieldValues, dataKeys.inquiryDate)) {
			baseGroup = removeRuleFromGroup(baseGroup, dataKeys.inquiryDate);
		}
		// add default inquiryDate rule
		baseGroup = insertRuleIntoGroup(baseGroup, getInquiryDateRule());

		// convert status labels to codes so validation & resubmit use the same parsed criteria
		const parsedCriteria = parseQueryFilterParams({ query: baseGroup, field: dataKeys.coverageStatus, parser: getStatus });

		return {
			filterParams: { criteria: parsedCriteria ?? baseGroup },
			fieldValuesSet: allFieldValues,
			hasFilterParams: hasParams,
		};
	}, [apiParams, batchResubmitMetaData.dateRange, coverageId]);

	const {
		data: validationData,
		isLoading,
		isFetching,
	} = useValidateBatchEligibilityResubmission(filterParams, {
		enabled: batchResubmitMetaData.visible && !!batchResubmitMetaData.dateRange.range,
	});

	const loading = isLoading || isFetching || previousBatches.isLoading || previousBatches.isFetching;
	const validationCount = validationData?.count || 0;
	const hasValidationCount = !!validationCount;

	// function to get invalid entries for batch resubmit
	const getInvalidStatusDetails = () => {
		return _.map(ELIGIBILITY_RESUBMIT_INVALID_STATUS, (key) => {
			const count = _.get(validationData?.statusCountMapping, [key], 0);
			return { key: _.get(ELIGIBILITY_RESPONSE_TAG_STATUS, [key])?.label, count };
		});
	};

	const invalidRequestStatusDetails = _.filter(getInvalidStatusDetails(), (item) => item.count > 0);

	// resubmit batch
	const resubmitData = () => {
		closeBatchResubmissionModal();
		mutate({
			model: {
				searchParams: filterParams,
				expectedTransactions: validationCount,
				dateOfService: prepareDateRange(batchResubmitMetaData.newDateRange),
			},
		});
	};

	const onDateRangeChange = (dateRange: EligibilityRequests.ISelectedDateMeta) => {
		setBatchResubmitMetaData({ ...batchResubmitMetaData, dateRange });
	};

	return (
		<>
			<Tooltip title={'Resubmit Requests'}>
				<Button
					data-testid={dataTestId}
					color='default'
					loading={isPending}
					onClick={() => setBatchResubmitMetaData({ ...batchResubmitMetaData, visible: true })}
					icon={<Redo />}
				/>
			</Tooltip>

			<Modal
				data-testid='batch-resubmit-modal'
				open={batchResubmitMetaData?.visible}
				closable={true}
				maskClosable={false}
				width={650}
				title={'Resubmit Eligibility requests'}
				className='eligibility-export-modal'
				okText={`Resubmit Requests (${validationCount})`}
				onOk={resubmitData}
				onCancel={closeBatchResubmissionModal}
				cancelButtonProps={{ disabled: isPending }}
				okButtonProps={{
					'data-testid': 'batch-resubmit-confirm-btn',
					loading: isPending,
					disabled: loading || blockResubmit || !hasValidationCount || _.isEmpty(batchResubmitMetaData.newDateRange),
				}}
			>
				<div className='mb-2 italic'>{'Only inquiries within the last 60 days are eligible for resubmission.'}</div>
				{batchResubmitMetaData?.visible && (
					<Row gutter={[0, 12]}>
						{hasFilterParams && renderFilterTags(fieldValuesSet)}
						{!hasFilterParams && (
							<ServiceDateOptions
								isBatchResubmit
								disabled={blockResubmit}
								exportMetaData={batchResubmitMetaData}
								updateDateRange={onDateRangeChange}
							/>
						)}
						{loading ? (
							<Space>
								<Spin spinning indicator={<LoadingOutlined />} />
								{'Kindly wait while we process your request.'}
							</Space>
						) : blockResubmit ? (
							<InProgressBatchAlertBar
								type='resubmit'
								hasInprogressBatch={hasInprogressBatch}
								hasScheduledBatch={hasScheduledBatch}
								batch={_.first(previousBatches?.data?.data)}
								className='w-full'
							/>
						) : (
							<>
								<Col>
									{batchResubmitMetaData.dateRange.range && (
										<Alert type={hasValidationCount ? 'success' : 'error'} message={getCountMessage(validationCount)} />
									)}
								</Col>
								{!_.isEmpty(invalidRequestStatusDetails) && (
									<Col span={24}>
										{_.map(invalidRequestStatusDetails, (invalidEntry, index) => (
											<Row gutter={8} key={index} style={{ color: RED_HEX_CODE }}>
												<Col>
													<CloseCircleOutlined />
												</Col>
												<Col>{`Skipped due to ${invalidEntry.key}: ${invalidEntry.count}`}</Col>
											</Row>
										))}
									</Col>
								)}
								{hasValidationCount && (
									<>
										<Divider dashed={true} />
										<Col span={24} style={headerTextStyle}>
											{'Choose New Service Dates:'}
										</Col>
										<Col span={24} className='my-auto'>
											<label className='mr-2.5'>{'Service Dates:'}</label>
											<RangePicker
												data-testid='batch-resubmit-date-range'
												allowClear={false}
												presets={customDatePresets}
												value={batchResubmitMetaData.newDateRange}
												onChange={(range) => {
													if (range) {
														setBatchResubmitMetaData({
															...batchResubmitMetaData,
															newDateRange: range,
														});
													}
												}}
											/>
										</Col>
									</>
								)}
							</>
						)}
					</Row>
				)}
			</Modal>
		</>
	);
};

export default BatchResubmitEligibilityRequests;
